import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";

export default function CartPage() {
  const { items, removeItem, updateQty, subtotal } = useCart();

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="cart-page-empty">
        <p className="tracking-overline text-[11px] uppercase opacity-60">Your Bag</p>
        <h1 className="font-display text-5xl mt-4">It's empty.</h1>
        <p className="opacity-70 mt-6">Begin by exploring the winter collection.</p>
        <Link to="/shop" className="btn-primary mt-10 inline-flex">Explore the Collection</Link>
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-6 sm:px-12 py-16" data-testid="cart-page">
      <p className="tracking-overline text-[11px] uppercase opacity-60">Your Bag</p>
      <h1 className="font-display text-5xl sm:text-6xl mt-3">Reviewing<br /><span className="italic">your selection.</span></h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mt-16">
        <div className="lg:col-span-8">
          <ul>
            {items.map((item) => (
              <li
                key={`${item.product_id}-${item.size}`}
                className="grid grid-cols-12 gap-6 py-8 border-b items-center"
                style={{ borderColor: "var(--line)" }}
                data-testid={`cartpage-item-${item.product_id}`}
              >
                <div className="col-span-4 sm:col-span-2 aspect-[4/5] overflow-hidden" style={{ background: "var(--line-soft)" }}>
                  <img src={item.image} alt={item.name} className="object-cover w-full h-full" />
                </div>
                <div className="col-span-8 sm:col-span-5">
                  <Link to={`/shop/${item.slug}`} className="font-display text-2xl leading-tight hover:opacity-70">{item.name}</Link>
                  <p className="text-[11px] uppercase tracking-[0.18em] opacity-60 mt-2">Size {item.size}</p>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <div className="inline-flex items-center border" style={{ borderColor: "var(--line)" }}>
                    <button onClick={() => updateQty(item.product_id, item.size, item.quantity - 1)} className="px-3 py-2" data-testid={`cartpage-dec-${item.product_id}`}>
                      <Minus size={12} />
                    </button>
                    <span className="px-3 text-sm">{item.quantity}</span>
                    <button onClick={() => updateQty(item.product_id, item.size, item.quantity + 1)} className="px-3 py-2" data-testid={`cartpage-inc-${item.product_id}`}>
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
                <div className="col-span-4 sm:col-span-2 text-right font-display text-xl">{formatPrice(item.price * item.quantity, item.currency)}</div>
                <div className="col-span-2 sm:col-span-1 text-right">
                  <button onClick={() => removeItem(item.product_id, item.size)} aria-label="remove" className="opacity-50 hover:opacity-100" data-testid={`cartpage-remove-${item.product_id}`}>
                    <X size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <aside className="lg:col-span-4">
          <div className="p-8 border" style={{ borderColor: "var(--line)", background: "var(--bone-muted)" }}>
            <p className="tracking-overline text-[11px] uppercase opacity-60 mb-6">Summary</p>
            <div className="flex justify-between text-sm py-2">
              <span className="opacity-70">Subtotal</span>
              <span data-testid="cartpage-subtotal">{formatPrice(subtotal, items[0]?.currency)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 italic opacity-60">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            <div className="flex justify-between font-display text-2xl pt-6 mt-3 border-t" style={{ borderColor: "var(--line)" }}>
              <span>Total</span>
              <span>{formatPrice(subtotal, items[0]?.currency)}</span>
            </div>
            <Link to="/checkout" className="btn-primary w-full mt-8" data-testid="cartpage-checkout-button">
              Proceed to Checkout
            </Link>
            <Link to="/shop" className="btn-ghost w-full mt-5 block text-center">Continue Shopping</Link>
          </div>
        </aside>
      </div>
    </main>
  );
}
